/**
 * Catalog of standard context variables exposed to flow rules.
 *
 * Each var declares its type AND, when applicable, its closed enum of
 * legitimate values. The rule builder uses these to render the right input
 * widget (datetime picker for timestamps, True/False select for booleans,
 * proper select for enums, number/text input otherwise).
 */

import { COUNTRIES } from "./countries";

export type VarType = "string" | "number" | "boolean" | "array" | "timestamp";

export interface ContextVar {
  name: string;
  type: VarType;
  /** Optional explainer shown next to the variable picker. */
  description?: string;
  /** Closed set of allowed values; renders the value input as a <select>. */
  options?: { value: string; label: string }[];
  /**
   * Optional unit hint shown under the value input in the rule builder.
   * Use for numeric variables whose units aren't obvious from the name —
   * cents-vs-dollars, milliseconds-vs-seconds, grams-vs-kilograms, etc.
   * Keep it short ("In cents (1000 = 10.00)") — it's a 1-line hint, not docs.
   */
  unitHint?: string;
}

const COMMON_CURRENCIES = [
  "EUR",
  "USD",
  "GBP",
  "SEK",
  "NOK",
  "DKK",
  "CHF",
  "JPY",
  "AUD",
  "CAD",
  "PLN",
  "CZK",
  "HUF",
  "RON",
  "BGN",
  "HRK",
  "ISK",
  "TRY",
  "BRL",
  "MXN",
  "INR",
  "SGD",
  "HKD",
  "AED",
];

const COUNTRY_OPTIONS = COUNTRIES.map((c) => ({
  value: c.code,
  label: `${c.name} (${c.code})`,
}));

const DAYS_OF_WEEK = [
  { value: "0", label: "Sunday" },
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
  { value: "6", label: "Saturday" },
];

const HOURS_OF_DAY = Array.from({ length: 24 }, (_, h) => ({
  value: String(h),
  label: `${h.toString().padStart(2, "0")}:00`,
}));

export const CONTEXT_VARS: readonly ContextVar[] = [
  {
    name: "buyer_country",
    type: "string",
    description: "ISO 3166-1 alpha-2 country code of the buyer.",
    options: COUNTRY_OPTIONS,
  },
  {
    name: "currency",
    type: "string",
    description: "Currency of the cart (ISO 4217).",
    options: COMMON_CURRENCIES.map((c) => ({ value: c, label: c })),
  },
  {
    name: "cart_total_raw",
    type: "number",
    description: "Cart subtotal in cents (before tax, shipping, discounts).",
    unitHint: "In cents · 1000 = 10.00",
  },
  {
    name: "line_item_count",
    type: "number",
    description: "Number of line items in the cart.",
  },
  {
    name: "source_type",
    type: "string",
    description: "Which adapter created this session.",
    options: [
      { value: "shopify", label: "Shopify" },
      { value: "api", label: "API" },
      { value: "email", label: "Email" },
      { value: "payment_link", label: "Payment link" },
      { value: "woocommerce", label: "WooCommerce" },
      { value: "prestashop", label: "PrestaShop" },
      { value: "custom", label: "Custom" },
    ],
  },
  {
    name: "source_ref",
    type: "string",
    description: "Adapter-specific reference (e.g. Shopify shop domain).",
  },
  {
    name: "current_time",
    type: "timestamp",
    description: "ISO 8601 timestamp at session creation (UTC).",
  },
  {
    name: "day_of_week",
    type: "number",
    description: "0 = Sunday, 6 = Saturday (UTC).",
    options: DAYS_OF_WEEK,
  },
  {
    name: "hour_of_day",
    type: "number",
    description: "0–23 (UTC). Merchant-local timezone support is a planned enhancement.",
    options: HOURS_OF_DAY,
  },
  {
    name: "device_type",
    type: "string",
    description: "Coarse device class derived from the buyer's User-Agent.",
    options: [
      { value: "desktop", label: "Desktop" },
      { value: "mobile", label: "Mobile" },
      { value: "tablet", label: "Tablet" },
    ],
  },
  {
    name: "customer_email_domain",
    type: "string",
    description: 'Domain part of the buyer email (e.g. "gmail.com").',
  },
  {
    name: "fraud_band",
    type: "string",
    description:
      "Fraud-check verdict (low | medium | high). Only populated AFTER a fraud_check node runs upstream — using this in a condition placed before fraud_check evaluates to empty.",
    options: [
      { value: "low", label: "Low" },
      { value: "medium", label: "Medium" },
      { value: "high", label: "High" },
    ],
  },
];

/*
 * Removed from the dropdown 2026-04-28 because the engine doesn't yet
 * populate them — listing them as options would silently produce
 * always-false rules (the cart_total_raw incident pattern):
 *
 *   utm_source, utm_campaign, utm_medium  — need TAE / source-side
 *     forwarding from the storefront entry URL into session.context
 *     before the engine can read them. Re-add when that's wired.
 *   returning_customer — needs a per-merchant past-orders lookup at
 *     session-create time. Re-add when that query lands.
 *
 * Existing flows that already reference these names continue to
 * evaluate to null/empty and short-circuit to false_next — the same
 * behaviour as before this cleanup; we're just no longer offering
 * them as new picks.
 */

export const CONTEXT_VARS_BY_NAME: Readonly<Record<string, ContextVar>> =
  Object.fromEntries(CONTEXT_VARS.map((v) => [v.name, v]));

export function varType(name: string): VarType {
  return CONTEXT_VARS_BY_NAME[name]?.type ?? "string";
}

export function varOptions(name: string): { value: string; label: string }[] | undefined {
  return CONTEXT_VARS_BY_NAME[name]?.options;
}

export function varUnitHint(name: string): string | undefined {
  return CONTEXT_VARS_BY_NAME[name]?.unitHint;
}
